import { useActiveProject } from '@hooks/useActiveProject';
import { getWalletStatus, type WalletStatus } from '@services/wallet.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { useSocket } from './useSocket';

/**
 * Drives the wallet blocker overlay + warning banner. Reads
 * GET /wallets/status for the active project and stays fresh by
 * invalidating on the same wallet:deducted / wallet:recharged socket
 * events the balance hook listens to — so a debit that drives the wallet
 * to zero flips the blocker on without a manual refresh, and a recharge
 * clears it.
 */
export function useWalletStatus(): {
  status: WalletStatus | undefined;
  isLoading: boolean;
} {
  const qc = useQueryClient();
  const { on, off } = useSocket();
  const { selectedProjectId } = useActiveProject();

  const { data, isLoading } = useQuery({
    queryKey: ['wallet', 'status', selectedProjectId],
    queryFn: () => getWalletStatus(selectedProjectId),
    staleTime: 15_000,
  });

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['wallet', 'status'] });
  }, [qc]);

  // `wallet:blocked` fires the instant a debit/pre-renewal pauses a project,
  // `billing:reactivated` / `wallet:recharged` when a recharge resumes it.
  // (`wallet:deducted` is wired but never emitted server-side, so it can't be
  // relied on to surface the blocker — the explicit pause/recharge events are.)
  useEffect(() => {
    const events = [
      'wallet:deducted',
      'wallet:recharged',
      'wallet:blocked',
      'billing:reactivated',
      'billing:stopped',
    ] as const;
    events.forEach((e) => on(e, invalidate));
    return () => {
      events.forEach((e) => off(e, invalidate));
    };
  }, [on, off, invalidate]);

  return { status: data, isLoading };
}
